import wikiData from './wikiLoadoutData'
import { getSurvivorGameplayPerkId } from './survivorPerkEquivalents'
import { survivorTagOverrides } from './survivorTagOverrides'
import survivorSynergyOverrides from './survivorSynergyOverrides'
import {
  getSurvivorPerkStrengthScore,
  getSurvivorPerkStrengthTier
} from './survivorPerkStrengths'
import { getSurvivorPerkInteractionKnowledge } from './survivorPerkInteractions'

const DEFAULT_PERK_DATA = {
  tags: [],
  soloQueueScore: 50,
  centralEligible: true,
  memeLevel: 0,
  constraints: {
    requiresItem: false,
    requiresTeamCoordination: false,
    requiresHeal: false,
    exhaustion: false
  },
  blacklistDefault: false,
  synergyWeights: {},
  diminishingGroups: [],
  antiSynergies: {},
  notes: ''
}

const survivorPerkMetadata = {
  ace_in_the_hole: {
    tags: ['item', 'chest', 'loot'],
    soloQueueScore: 45,
    synergyWeights: {
      appraisal: 95,
      plunderers_instinct: 80,
      pharmacy: 65
    }
  },
  appraisal: {
    tags: ['item', 'chest', 'loot'],
    soloQueueScore: 48,
    synergyWeights: {
      plunderers_instinct: 85,
      ace_in_the_hole: 95
    }
  },
  bond: {
    tags: ['support', 'information', 'solo_queue'],
    soloQueueScore: 78,
    synergyWeights: {
      well_make_it: 72,
      leader: 55
    }
  },
  botany_knowledge: {
    tags: ['support', 'heal'],
    soloQueueScore: 70,
    constraints: {
      requiresHeal: true
    },
    synergyWeights: {
      well_make_it: 72,
      empathy: 62
    }
  },
  built_to_last: {
    tags: ['item', 'resource'],
    soloQueueScore: 64,
    constraints: {
      requiresItem: true
    },
    synergyWeights: {
      streetwise: 88,
      scavenger: 74
    }
  },
  dance_with_me: {
    tags: ['chase', 'stealth', 'escape'],
    soloQueueScore: 60,
    synergyWeights: {
      lithe: 94,
      quick_and_quiet: 82
    }
  },
  deception: {
    tags: ['chaos', 'misdirection', 'stealth'],
    soloQueueScore: 56,
    memeLevel: 40,
    synergyWeights: {
      diversion: 74,
      head_on: 70,
      quick_and_quiet: 67
    }
  },
  distortion: {
    tags: ['stealth', 'information', 'solo_queue'],
    soloQueueScore: 75,
    synergyWeights: {
      calm_spirit: 48,
      lightweight: 45
    }
  },
  diversion: {
    tags: ['chaos', 'misdirection', 'stealth'],
    soloQueueScore: 55,
    memeLevel: 55,
    synergyWeights: {
      red_herring: 95,
      blast_mine: 85,
      deception: 75,
      lightweight: 60
    }
  },
  empathy: {
    tags: ['support', 'information', 'heal'],
    soloQueueScore: 66,
    synergyWeights: {
      botany_knowledge: 62,
      solidarity: 58
    }
  },
  flashbang: {
    tags: ['item', 'support', 'chaos'],
    soloQueueScore: 58,
    synergyWeights: {
      background_player: 85,
      champion_of_light: 75
    }
  },
  head_on: {
    tags: ['chase', 'chaos', 'stun'],
    soloQueueScore: 52,
    memeLevel: 35,
    constraints: {
      exhaustion: true
    },
    synergyWeights: {
      quick_and_quiet: 92,
      deception: 70
    }
  },
  kindred: {
    tags: ['support', 'information', 'solo_queue', 'macro'],
    soloQueueScore: 92,
    synergyWeights: {
      open_handed: 95,
      well_make_it: 58
    }
  },
  lightweight: {
    tags: ['stealth', 'escape'],
    soloQueueScore: 65,
    synergyWeights: {
      lucky_break: 62,
      diversion: 60
    }
  },
  lithe: {
    tags: ['chase', 'escape'],
    soloQueueScore: 84,
    constraints: {
      exhaustion: true
    },
    synergyWeights: {
      dance_with_me: 94,
      quick_and_quiet: 78,
      resilience: 55
    }
  },
  no_mither: {
    tags: ['meme', 'injured'],
    soloQueueScore: 12,
    memeLevel: 90,
    antiSynergies: {
      self_care: {
        severity: 'hard',
        reason: 'No Mither prevents standard self-healing value.'
      },
      inner_strength: {
        severity: 'hard',
        reason: 'No Mither keeps you Broken, so self-heal completion perks cannot deliver their main value.'
      },
      strength_in_shadows: {
        severity: 'hard',
        reason: 'No Mither keeps you Broken, so basement self-healing cannot complete.'
      },
      dramaturgy: {
        severity: 'hard',
        reason: 'Dramaturgy requires being Healthy; No Mither starts and keeps you Injured.'
      },
      overcome: {
        severity: 'hard',
        reason: 'Overcome requires losing a health state from Healthy; No Mither prevents that trigger pattern.'
      },
      for_the_people: {
        severity: 'hard',
        reason: 'For the People requires being Healthy; No Mither keeps you Injured and Broken.'
      }
    }
  },
  open_handed: {
    tags: ['support', 'information', 'aura'],
    soloQueueScore: 55,
    synergyWeights: {
      kindred: 95,
      bond: 68
    }
  },
  pharmacy: {
    tags: ['item', 'heal', 'chest'],
    soloQueueScore: 48,
    synergyWeights: {
      ace_in_the_hole: 65,
      plunderers_instinct: 60
    }
  },
  plunderers_instinct: {
    tags: ['item', 'chest', 'loot'],
    soloQueueScore: 45,
    synergyWeights: {
      appraisal: 85,
      ace_in_the_hole: 80,
      pharmacy: 60
    }
  },
  quick_and_quiet: {
    tags: ['chase', 'stealth', 'escape'],
    soloQueueScore: 62,
    synergyWeights: {
      head_on: 92,
      dance_with_me: 82,
      lithe: 78
    }
  },
  red_herring: {
    tags: ['chaos', 'misdirection', 'generator'],
    soloQueueScore: 45,
    memeLevel: 58,
    synergyWeights: {
      diversion: 95,
      blast_mine: 94,
      deception: 65
    }
  },
  blast_mine: {
    tags: ['chaos', 'generator', 'information'],
    soloQueueScore: 72,
    memeLevel: 30,
    synergyWeights: {
      red_herring: 94,
      diversion: 85,
      wiretap: 75
    }
  },
  sprint_burst: {
    tags: ['chase', 'escape', 'macro'],
    soloQueueScore: 86,
    constraints: {
      exhaustion: true
    },
    synergyWeights: {
      vigil: 74,
      fixated: 65
    }
  },
  streetwise: {
    tags: ['item', 'resource', 'loot', 'aura', 'support'],
    soloQueueScore: 60,
    constraints: {
      requiresItem: true
    },
    synergyWeights: {
      built_to_last: 88,
      scavenger: 72
    }
  },
  vigil: {
    tags: ['chase', 'recovery', 'support'],
    soloQueueScore: 68,
    synergyWeights: {
      sprint_burst: 74,
      lithe: 55
    }
  },
  well_make_it: {
    tags: ['support', 'heal', 'solo_queue'],
    soloQueueScore: 86,
    constraints: {
      requiresHeal: true
    },
    synergyWeights: {
      botany_knowledge: 72,
      bond: 72,
      kindred: 58
    }
  },
  windows_of_opportunity: {
    tags: ['chase', 'information', 'solo_queue'],
    soloQueueScore: 92,
    notes: 'Configured as a non-influential default lock in V1.'
  }
}

const SURVIVOR_WIKI_ID_ALIASES = {
  baby_sitter: 'babysitter',
  circle_of_healing: 'boon_circle_of_healing',
  dark_theory: 'boon_dark_theory',
  detectives_hunch: 'detective_s_hunch',
  exponential: 'boon_exponential',
  plunderers_instinct: 'plunderer_s_instinct',
  quick_and_quiet: 'quick_quiet',
  shadow_step: 'boon_shadow_step',
  treacherous_crows: 'invocation_treacherous_crows',
  well_make_it: 'we_ll_make_it',
  were_gonna_live_forever: 'we_re_gonna_live_forever'
}

const SURVIVOR_INTERNAL_ID_ALIASES = Object.keys(SURVIVOR_WIKI_ID_ALIASES).reduce((aliases, internalId) => {
  aliases[SURVIVOR_WIKI_ID_ALIASES[internalId]] = internalId
  return aliases
}, {})

function toWikiPerkId (id) {
  return SURVIVOR_WIKI_ID_ALIASES[id] || id
}

function toInternalPerkId (id) {
  return SURVIVOR_INTERNAL_ID_ALIASES[id] || id
}

function toPerkId (spriteKey) {
  return spriteKey
    .replace(/^\d+_/, '')
    .replace(/-/g, '_')
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase()
}

function mergePerkData (rawPerk) {
  const id = rawPerk.id || toPerkId(rawPerk.name)
  const wikiId = rawPerk.wikiId || toWikiPerkId(id)
  const wikiEntry = rawPerk.wikiEntry || wikiData.survivorPerks.find(perk => perk.id === wikiId) || null
  const gameplayId = getSurvivorGameplayPerkId(id)
  const metadata = survivorPerkMetadata[id] || survivorPerkMetadata[gameplayId] || {}
  const tagOverride = survivorTagOverrides[id] || survivorTagOverrides[gameplayId]
  const synergyOverride = survivorSynergyOverrides[id] || survivorSynergyOverrides[gameplayId] || {}
  const soloQueueScore = metadata.soloQueueScore === undefined ? DEFAULT_PERK_DATA.soloQueueScore : metadata.soloQueueScore
  const strengthScore = getSurvivorPerkStrengthScore(gameplayId, soloQueueScore)

  return {
    id: id,
    gameplayId: gameplayId,
    wikiId: wikiId,
    index: rawPerk.index,
    name: rawPerk.name,
    spriteKey: rawPerk.hasSprite === false ? null : rawPerk.name,
    hasSprite: rawPerk.hasSprite !== false,
    displayName: metadata.displayName || (wikiEntry && wikiEntry.name) || null,
    displayNameKey: `perks.survivor.${rawPerk.name}`,
    tags: tagOverride || metadata.tags || (wikiEntry && wikiEntry.tags) || DEFAULT_PERK_DATA.tags,
    wikiTags: (wikiEntry && wikiEntry.tags) || [],
    patch: wikiEntry && wikiEntry.patch,
    values: (wikiEntry && wikiEntry.values) || [],
    units: (wikiEntry && wikiEntry.units) || [],
    mechanics: (wikiEntry && wikiEntry.mechanics) || {},
    soloQueueScore: soloQueueScore,
    strengthScore: strengthScore,
    strengthTier: getSurvivorPerkStrengthTier(strengthScore),
    centralEligible: metadata.centralEligible === undefined ? DEFAULT_PERK_DATA.centralEligible : metadata.centralEligible,
    memeLevel: metadata.memeLevel === undefined ? DEFAULT_PERK_DATA.memeLevel : metadata.memeLevel,
    constraints: Object.assign({}, DEFAULT_PERK_DATA.constraints, metadata.constraints),
    blacklistDefault: metadata.blacklistDefault === undefined ? DEFAULT_PERK_DATA.blacklistDefault : metadata.blacklistDefault,
    synergyWeights: synergyOverride.synergyWeights || metadata.synergyWeights || DEFAULT_PERK_DATA.synergyWeights,
    diminishingGroups: synergyOverride.diminishingGroups || metadata.diminishingGroups || DEFAULT_PERK_DATA.diminishingGroups,
    antiSynergies: metadata.antiSynergies || DEFAULT_PERK_DATA.antiSynergies,
    interactionKnowledge: getSurvivorPerkInteractionKnowledge(gameplayId),
    notes: metadata.notes || DEFAULT_PERK_DATA.notes
  }
}

export function createSurvivorCatalog (rawPerks) {
  const catalog = rawPerks.filter(Boolean).map(mergePerkData)
  const knownWikiIds = new Set(catalog.map(perk => perk.wikiId || perk.id))
  const wikiOnly = wikiData.survivorPerks
    .filter(perk => !knownWikiIds.has(perk.id))
    .map((perk, offset) => mergePerkData({
      id: toInternalPerkId(perk.id),
      wikiId: perk.id,
      index: catalog.length + offset,
      name: perk.id,
      hasSprite: false,
      wikiEntry: perk
    }))
  return catalog.concat(wikiOnly)
}

export { toPerkId }
